import {convertDoseLimits} from '@cypro/engine/utils'
import type {DoseLimitsMatrix} from '@cypro/engine/database/schema'
import {toaster} from '@cypro/ui/toaster'
import {useTranslate} from '@tolgee/react'
import {useEffect, useRef} from 'react'
import {useGetDrugCatalogUsage} from './use-get-drug-catalog-usage'

interface DoseUnitFieldOptions {
  drugId: string | undefined
  unitId: string | undefined
  units: readonly {id: string; ucumCode: string | null}[] | undefined
  getDoseLimits: () => DoseLimitsMatrix
  setDoseLimits: (limits: DoseLimitsMatrix) => void
  restoreUnit: (unitId: string) => void
}

/**
 * Dose unit field of a substance or supportive medication (CY-369): locked once a prescription or protocol
 * uses the drug, and while it may change, the dose limits are converted with it.
 */
export function useDoseUnitField({drugId, unitId, units, getDoseLimits, setDoseLimits, restoreUnit}: DoseUnitFieldOptions) {
  const {t} = useTranslate('common')
  const {prescriptions, protocols, isUsed, isLoading} = useGetDrugCatalogUsage(drugId)

  // the unit before a change: the listener runs before the effect updates it
  const previousUnitId = useRef(unitId)
  useEffect(() => {
    previousUnitId.current = unitId
  }, [unitId])

  function onUnitChange(nextUnitId: string | undefined) {
    const previous = previousUnitId.current
    if (!previous || !nextUnitId || previous === nextUnitId) return

    const from = units?.find(unit => unit.id === previous)?.ucumCode
    const to = units?.find(unit => unit.id === nextUnitId)?.ucumCode
    const result = convertDoseLimits(getDoseLimits(), from, to)
    if (result.ok) {
      setDoseLimits(result.limits)
      return
    }
    restoreUnit(previous)
    toaster.error({title: t('error'), description: t('dose_unit_clear_limits')})
  }

  return {
    disabled: isLoading || isUsed,
    helpText: isUsed ? t('dose_unit_locked', {prescriptions, protocols}) : undefined,
    onUnitChange,
  }
}
